import { cn } from '@/lib/utils';
import type { RentalHandoverPhoto } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';
import { getHandoverPhotoContentUrl, listHandoverPhotos } from '../../../api/rental-handover';

/**
 * PV-KAUCIO-001 fotó-MVP — a kiadáskori (HANDOVER) és visszavételi (RETURN) állapot-fotók
 * egymás melletti, READ-ONLY összevetése a kaució-elszámoláshoz. A meglévő handover-fotó
 * stackre épül (`listHandoverPhotos` kind-szűrővel) — 0 BE-change. Cél: a kiadáskor már
 * meglévő sérülés ne terhelje a bérlőt (átvétel↔visszavétel vizuális diff).
 * A strukturált per-tartozék kondíció-snapshot Phase-2 (Lexia↔Zsuzsi tisztázza, kell-e).
 */
function PhotoColumn({
  title,
  photos,
  rentalId,
  emptyLabel,
  testid,
}: {
  title: string;
  photos: RentalHandoverPhoto[];
  rentalId: string;
  emptyLabel: string;
  testid: string;
}) {
  return (
    <div data-testid={testid}>
      <h4 className="mb-2 text-sm font-medium">{title}</h4>
      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
          {emptyLabel}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {photos.map(photo => (
            <li key={photo.id} data-testid={`${testid}-item-${photo.id}`}>
              <img
                src={getHandoverPhotoContentUrl(rentalId, photo.id)}
                alt={photo.fileName}
                className="aspect-square w-full rounded-md border object-cover"
                loading="lazy"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function HandoverReturnPhotoCompare({
  rentalId,
  className,
}: {
  rentalId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [handoverPhotos, setHandoverPhotos] = useState<RentalHandoverPhoto[]>([]);
  const [returnPhotos, setReturnPhotos] = useState<RentalHandoverPhoto[]>([]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const [handover, ret] = await Promise.all([
        listHandoverPhotos(rentalId, 'HANDOVER'),
        listHandoverPhotos(rentalId, 'RETURN'),
      ]);
      if (active) {
        setHandoverPhotos(handover);
        setReturnPhotos(ret);
      }
    })();
    return () => {
      active = false;
    };
  }, [rentalId]);

  // Ha egyik fázisban sincs fotó, nincs mit összevetni → ne foglaljon helyet.
  if (handoverPhotos.length === 0 && returnPhotos.length === 0) return null;

  return (
    <div className={cn('space-y-3', className)} data-testid="handover-return-compare">
      <div>
        <h3 className="text-sm font-medium">{t('rental.handover.compareTitle')}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{t('rental.handover.compareHint')}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PhotoColumn
          title={t('rental.handover.handoverColumn')}
          photos={handoverPhotos}
          rentalId={rentalId}
          emptyLabel={t('rental.handover.empty')}
          testid="compare-handover"
        />
        <PhotoColumn
          title={t('rental.handover.returnColumn')}
          photos={returnPhotos}
          rentalId={rentalId}
          emptyLabel={t('rental.handover.empty')}
          testid="compare-return"
        />
      </div>
    </div>
  );
}
